import React from 'react';
import { 
  Box, 
  Stack, 
  TextField, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Chip 
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Event, EventCategory, LocationType } from '@/types/events';
import { EVENT_TYPES, LOCATION_TYPES } from './constants';

interface EditModeProps {
  event: Event;
  onChange: (event: Event) => void;
}

export const EditMode: React.FC<EditModeProps> = ({ event, onChange }) => {
  const handleChange = (field: keyof Event) => (
    e: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    onChange({
      ...event,
      [field]: e.target.value
    });
  };

  const handleDateChange = (field: 'startDate' | 'endDate') => (date: Date | null) => {
    if (date) {
      onChange({
        ...event,
        [field]: date.toISOString()
      });
    }
  };

  const handleGradeToggle = (grade: number) => {
    onChange({
      ...event,
      targetGrades: event.targetGrades.includes(grade)
        ? event.targetGrades.filter(g => g !== grade)
        : [...event.targetGrades, grade]
    });
  };

  const changeCategory = (field: EventCategory) => {
    onChange({
      ...event,
      eventCategory: field,
    });
  };

  const changeLocationType = (field: LocationType) => {
    onChange({
      ...event,
      locationType: field,
    });
  };

  const handleObjectiveAdd = () => {
    const objective = prompt('Enter new objective:');
    if (objective) {
      onChange({
        ...event,
        objectives: [...event.objectives, objective]
      });
    }
  };

  const handleObjectiveRemove = (index: number) => {
    onChange({
      ...event,
      objectives: event.objectives.filter((_, i) => i !== index)
    });
  };

  return (
    <Stack spacing={3}>
      <TextField
        label="Title"
        value={event.title}
        onChange={handleChange('title')}
        fullWidth
        required
      />

      <TextField
        label="Description"
        value={event.description}
        onChange={handleChange('description')}
        multiline
        rows={3}
        fullWidth
        required
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DateTimePicker
            label="Start Date & Time"
            value={new Date(event.startDate)}
            onChange={handleDateChange('startDate')}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateTimePicker
            label="End Date & Time"
            value={new Date(event.endDate)}
            onChange={handleDateChange('endDate')}
            sx={{ width: '100%' }}
          />
        </Grid>
      </Grid>

      <FormControl fullWidth>
        <InputLabel>Event Type</InputLabel>
        <Select
          value={event.eventCategory}
          label="Event Type"
          onChange={(event) => changeCategory(event.target.value as EventCategory)}
        >
          {EVENT_TYPES.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Location Type</InputLabel>
        <Select
          value={event.locationType}
          label="Location Type"
          onChange={(event) => changeLocationType(event.target.value as LocationType)}
        >
          {LOCATION_TYPES.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Location"
        value={event.location}
        onChange={handleChange('location')}
        fullWidth
      />

      {(event.locationType === 'virtual' || event.locationType === 'hybrid') && (
        <TextField
          label="Virtual Meeting Link"
          value={event.virtualLink}
          onChange={handleChange('virtualLink')}
          fullWidth
        />
      )}

      <Box>
        <Box component="h4" sx={{ typography: 'subtitle2', mb: 1 }}>
          Target Grades
        </Box>
        <Stack direction="row" spacing={1}>
          {Array.from({ length: 4 }, (_, i) => i + 9).map((grade) => (
            <Chip
              key={grade}
              label={`Grade ${grade}`}
              onClick={() => handleGradeToggle(grade)}
              color={event.targetGrades.includes(grade) ? 'primary' : 'default'}
              variant={event.targetGrades.includes(grade) ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box component="h4" sx={{ typography: 'subtitle2', m: 0 }}>
            Objectives
          </Box>
          <Button
            size="small"
            onClick={handleObjectiveAdd}
          >
            Add Objective
          </Button>
        </Box>
        <Stack spacing={1}>
          {event.objectives.map((objective, index) => (
            <Chip
              key={index}
              label={objective}
              onDelete={() => handleObjectiveRemove(index)}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};